import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Download, Upload, Plus, Trash2, Save, Users } from 'lucide-react';

interface SiteOption { id: string; name?: string; site_reference?: string }
interface Contact {
  id: string;
  site_id: string;
  site_name: string;
  site_reference: string;
  contact_name: string;
  contact_surname: string;
  contact_email: string;
  contact_number: string;
}
type NewContact = Omit<Contact, 'id' | 'site_name' | 'site_reference'>;

const EMPTY_NEW: NewContact = { site_id: '', contact_name: '', contact_surname: '', contact_email: '', contact_number: '' };

export const SiteContactsTab: React.FC<{ clientId: string; sites: SiteOption[] }> = ({ clientId, sites }) => {
  const [rows, setRows] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newRow, setNewRow] = useState<NewContact>(EMPTY_NEW);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<Contact[]>(`/clients/${clientId}/site-contacts`);
      setRows(r.data);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  const patchRow = (id: string, field: keyof Contact, value: string) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const saveRow = async (row: Contact) => {
    setBusy(true);
    try {
      await api.patch(`/site-contacts/${row.id}`, {
        contact_name: row.contact_name,
        contact_surname: row.contact_surname,
        contact_email: row.contact_email,
        contact_number: row.contact_number,
      });
      toast.success('Contact saved.');
    } catch {
      toast.error('Could not save contact.');
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (id: string) => {
    setBusy(true);
    try {
      await api.delete(`/site-contacts/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success('Contact removed.');
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    if (!newRow.site_id || !newRow.contact_name.trim()) {
      toast.error('Pick a site and enter a contact name.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/site-contacts`, newRow);
      setNewRow(EMPTY_NEW);
      await load();
      toast.success('Contact added.');
    } catch {
      toast.error('Could not add contact.');
    } finally {
      setBusy(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/site-contacts/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `site-contacts-${clientId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed.');
    }
  };

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post<{ created: number; updated: number; skipped: number; unmatched_site_reference: number }>(
        `/clients/${clientId}/site-contacts/import`, fd);
      const d = r.data;
      toast.success(`Imported: ${d.created} new, ${d.updated} updated${d.unmatched_site_reference ? `, ${d.unmatched_site_reference} unmatched refs` : ''}.`);
      await load();
    } catch {
      toast.error('Import failed. Use the exported template.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card data-testid="site-contacts-card">
      <CardHeader className="flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Users size={16} /> Site Contacts</CardTitle>
          <CardDescription>People who receive the survey link (email/SMS) — not platform users. A site can have several contacts.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onImportFile} data-testid="site-contacts-import-input" />
          <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="site-contacts-export-btn"><Download size={14} className="mr-1" /> Export</Button>
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="site-contacts-import-btn"><Upload size={14} className="mr-1" /> Import</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add new */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center p-3 rounded-lg bg-raptor-navy/5" data-testid="site-contacts-add-row">
          <select
            className="h-9 rounded-md border border-raptor-navy/20 bg-white px-2 text-sm font-body"
            value={newRow.site_id}
            onChange={(e) => setNewRow((n) => ({ ...n, site_id: e.target.value }))}
            data-testid="new-contact-site"
          >
            <option value="">Select site…</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name || s.id}{s.site_reference ? ` (${s.site_reference})` : ''}</option>
            ))}
          </select>
          <Input placeholder="Name" value={newRow.contact_name} onChange={(e) => setNewRow((n) => ({ ...n, contact_name: e.target.value }))} data-testid="new-contact-name" />
          <Input placeholder="Surname" value={newRow.contact_surname} onChange={(e) => setNewRow((n) => ({ ...n, contact_surname: e.target.value }))} data-testid="new-contact-surname" />
          <Input placeholder="Email" value={newRow.contact_email} onChange={(e) => setNewRow((n) => ({ ...n, contact_email: e.target.value }))} data-testid="new-contact-email" />
          <Input placeholder="Number" value={newRow.contact_number} onChange={(e) => setNewRow((n) => ({ ...n, contact_number: e.target.value }))} data-testid="new-contact-number" />
          <Button size="sm" variant="gradient" onClick={addRow} disabled={busy} data-testid="new-contact-add"><Plus size={14} className="mr-1" /> Add</Button>
        </div>

        {/* Header */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-1 text-[11px] uppercase tracking-wide text-raptor-navy/50 font-body">
          <div className="col-span-3">Site</div>
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Surname</div>
          <div className="col-span-2">Email</div>
          <div className="col-span-2">Number</div>
          <div className="col-span-1" />
        </div>

        {loading ? (
          <div className="text-sm text-raptor-navy/50 font-body py-6 text-center">Loading contacts…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-raptor-navy/50 font-body py-6 text-center" data-testid="site-contacts-empty">No site contacts yet. Add one above or import the template.</div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`site-contact-row-${row.id}`}>
              <div className="md:col-span-3 text-sm font-body text-raptor-navy">
                <div className="font-medium">{row.site_name}</div>
                <div className="text-[11px] text-raptor-navy/50">Ref {row.site_reference || '—'}</div>
              </div>
              <Input className="md:col-span-2" value={row.contact_name} onChange={(e) => patchRow(row.id, 'contact_name', e.target.value)} />
              <Input className="md:col-span-2" value={row.contact_surname} onChange={(e) => patchRow(row.id, 'contact_surname', e.target.value)} />
              <Input className="md:col-span-2" value={row.contact_email} onChange={(e) => patchRow(row.id, 'contact_email', e.target.value)} />
              <Input className="md:col-span-2" value={row.contact_number} onChange={(e) => patchRow(row.id, 'contact_number', e.target.value)} />
              <div className="md:col-span-1 flex items-center gap-1 justify-end">
                <Button size="icon" variant="ghost" onClick={() => saveRow(row)} disabled={busy} data-testid={`save-contact-${row.id}`} title="Save"><Save size={15} /></Button>
                <Button size="icon" variant="ghost" onClick={() => deleteRow(row.id)} disabled={busy} data-testid={`delete-contact-${row.id}`} title="Remove"><Trash2 size={15} className="text-red-500" /></Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};
